import { useState } from 'react';
import { TOPIC_OPTIONS, VERSION_OPTIONS } from '../constants/content';
import { submitLead } from '../utils/submitLead';

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  version: '',
  topic: '',
  message: '',
};

export default function LeadForm({
  variant = 'compact',
  title = 'Get your error diagnosed',
  subtitle = 'Send the details and an expert replies by email.',
  cta = 'Request a free review',
  formName = 'hero',
}) {
  const [values, setValues] = useState(EMPTY);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const update = (field) => (event) =>
    setValues((prev) => ({ ...prev, [field]: event.target.value }));

  async function handleSubmit(event) {
    event.preventDefault();
    if (status === 'sending') return;

    setStatus('sending');
    setError('');

    try {
      await submitLead({
        ...values,
        form: formName,
        source: 'booksexperts',
      });
      setStatus('sent');
      setValues(EMPTY);
    } catch (err) {
      setStatus('error');
      setError(err.message || 'booksexperts');
    }
  }

  if (status === 'sent') {
    return (
      <div className={`lead-form lead-form--${variant} lead-form--done`}>
        <div className="lead-form__tick" aria-hidden="true">
          ✓
        </div>
        <h3>Request received</h3>
        <p>
          A certified bookkeeping expert will contact you shortly. For faster help, start a
          live chat and quote your error code.
        </p>
        <button type="button" className="btn btn--ghost" onClick={() => setStatus('idle')}>
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form className={`lead-form lead-form--${variant}`} onSubmit={handleSubmit} noValidate>
      <div className="lead-form__head">
        <h3>{title}</h3>
        <p>{subtitle}</p>
      </div>

      <label className="field">
        <span>Full name</span>
        <input
          type="text"
          name="name"
          value={values.name}
          onChange={update('name')}
          placeholder="Alex Morgan"
          required
        />
      </label>

      <label className="field">
        <span>Email address</span>
        <input
          type="email"
          name="email"
          value={values.email}
          onChange={update('email')}
          placeholder="alex@business.com"
          required
        />
      </label>

      <label className="field">
        <span>Phone number (optional)</span>
        <input
          type="tel"
          name="phone"
          value={values.phone}
          onChange={update('phone')}
          placeholder="Only if you would rather be called"
        />
      </label>

      <label className="field">
        <span>Accounting software version</span>
        <select name="version" value={values.version} onChange={update('version')}>
          <option value="">Select a version</option>
          {VERSION_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>

      {variant === 'full' ? (
        <label className="field">
          <span>What do you need help with?</span>
          <select name="topic" value={values.topic} onChange={update('topic')}>
            <option value="">Select a topic</option>
            {TOPIC_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label className="field">
        <span>Describe the error</span>
        <textarea
          name="message"
          rows={variant === 'full' ? 4 : 3}
          value={values.message}
          onChange={update('message')}
          placeholder="Error code, when it appears and what you were doing"
        />
      </label>

      {status === 'error' ? (
        <p className="lead-form__error" role="alert">
          {error}
        </p>
      ) : null}

      <button type="submit" className="btn btn--green btn--block" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : cta}
      </button>

      <p className="lead-form__note">
        We use your details only to answer this request. No spam, no shared lists.
      </p>
    </form>
  );
}
